/* ==========================================================================
   AKQURAE — theme behaviour

   The cart talks to Shopify's AJAX Cart API, so the cart Liquid object, the
   checkout, and any discount/Function the merchant installs all stay in sync.
   Nothing about pricing is decided here — Shopify owns money. This file only
   ever displays what /cart.js returns.

   Checkout: we never build a checkout URL by hand. Every path to checkout —
   the cart page, the drawer, and Buy now — goes through a real
   <button name="checkout"> inside a form POSTing to {{ routes.cart_url }}.
   That is what Shopify expects, and it is also the element one-page checkout
   apps (Razorpay Magic Checkout) bind to. Navigate with window.location and
   those apps never fire.
   ========================================================================== */
(function () {
  'use strict';

  var $  = function (s, r) { return (r || document).querySelector(s); };
  var $$ = function (s, r) { return Array.prototype.slice.call((r || document).querySelectorAll(s)); };

  var cfg = window.AKQ || {};

  /* ----------------------------------------------------------------------
     Money — mirrors Shopify's money filter for client-rendered fragments.
     Cart API returns integer paise/cents, so everything divides by 100.
     ---------------------------------------------------------------------- */
  function money(cents) {
    var amount = (cents / 100).toFixed(2).replace(/\.00$/, '');
    // en-IN grouping: 1,23,456 rather than 123,456
    var parts = amount.split('.');
    var n = parts[0];
    var last3 = n.slice(-3);
    var rest = n.slice(0, -3);
    if (rest) last3 = ',' + last3;
    n = rest.replace(/\B(?=(\d{2})+(?!\d))/g, ',') + last3;
    return '₹' + n + (parts[1] ? '.' + parts[1] : '');
  }

  /* ----------------------------------------------------------------------
     Toasts
     ---------------------------------------------------------------------- */
  var Toast = {
    host: null,
    show: function (msg) {
      if (!this.host) {
        this.host = document.createElement('div');
        this.host.className = 'toasts';
        this.host.setAttribute('role', 'status');
        this.host.setAttribute('aria-live', 'polite');
        document.body.appendChild(this.host);
      }
      var el = document.createElement('div');
      el.className = 'toast';
      el.textContent = msg;
      this.host.appendChild(el);
      setTimeout(function () {
        el.classList.add('is-out');
        setTimeout(function () { el.remove(); }, 320);
      }, 3200);
    }
  };

  /* ----------------------------------------------------------------------
     Overlay manager — drawer, mobile nav, modals
     ---------------------------------------------------------------------- */
  var Drawer = {
    scrim: null, current: null, _last: null,
    init: function () {
      this.scrim = document.createElement('div');
      this.scrim.className = 'scrim';
      this.scrim.addEventListener('click', this.close.bind(this, false));
      document.body.appendChild(this.scrim);
      document.addEventListener('keydown', function (e) {
        if (e.key === 'Escape') Drawer.close();
        if (e.key === 'Tab' && Drawer.current) Drawer._trap(e);
      });
    },
    open: function (name) {
      var el = document.querySelector('[data-overlay="' + name + '"]');
      if (!el) return;
      this._last = document.activeElement;
      this.close(true);
      el.classList.add('is-open');
      el.removeAttribute('aria-hidden');
      this.scrim.classList.add('is-open');
      document.documentElement.classList.add('is-locked');
      this.current = el;
      var f = el.querySelector('[data-autofocus], button, a[href], input');
      if (f) setTimeout(function () { f.focus(); }, 60);
    },
    close: function (silent) {
      $$('[data-overlay]').forEach(function (el) {
        el.classList.remove('is-open');
        el.setAttribute('aria-hidden', 'true');
      });
      if (silent !== true) {
        this.scrim.classList.remove('is-open');
        document.documentElement.classList.remove('is-locked');
        if (this._last) this._last.focus();
      }
      this.current = null;
    },
    _trap: function (e) {
      var f = $$('a[href],button:not([disabled]),input,select,textarea,[tabindex]:not([tabindex="-1"])', this.current)
        .filter(function (el) { return el.offsetParent !== null; });
      if (!f.length) return;
      var first = f[0], last = f[f.length - 1];
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    }
  };

  /* ----------------------------------------------------------------------
     Cart — thin wrapper over the Shopify AJAX API.
     Every mutation re-reads /cart.js and repaints from that single source,
     rather than trusting a local copy that can drift from Shopify's.
     ---------------------------------------------------------------------- */
  var Cart = {
    state: null,

    fetch: function () {
      return fetch(cfg.routes.cart, { headers: { 'Accept': 'application/json' } })
        .then(function (r) { return r.json(); })
        .then(function (c) { Cart.state = c; Cart.paint(); return c; });
    },

    add: function (variantId, qty, opts) {
      opts = opts || {};
      return fetch(cfg.routes.cart_add, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({ items: [{ id: Number(variantId), quantity: qty || 1 }] })
      })
        .then(function (r) { return r.json().then(function (b) { return { ok: r.ok, body: b }; }); })
        .then(function (res) {
          if (!res.ok) {
            /* Shopify returns 422 with a human message when a variant is out of
               stock or the requested quantity exceeds inventory. Surface it. */
            Toast.show(res.body.description || res.body.message || cfg.strings.error);
            return null;
          }
          return Cart.fetch().then(function (c) {
            if (!opts.silent) {
              Toast.show(cfg.strings.added);
              Drawer.open('cart');
            }
            return c;
          });
        })
        .catch(function () { Toast.show(cfg.strings.error); });
    },

    change: function (key, qty) {
      return fetch(cfg.routes.cart_change, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({ id: key, quantity: qty })
      })
        .then(function (r) { return r.json(); })
        .then(function (c) { Cart.state = c; Cart.paint(); return c; })
        .catch(function () { Toast.show(cfg.strings.error); });
    },

    /* Hand off to checkout.

       This must go through a real <button name="checkout"> inside a cart form,
       not window.location. One-page checkout apps (Razorpay Magic Checkout and
       friends) bind a click handler to that button; a programmatic navigation
       is invisible to them, so the shopper silently gets Shopify's own checkout
       instead. Clicking the button dispatches a real click the app can see, and
       if no such app is installed the form POST reaches Shopify exactly as it
       always did. Falls back to a plain redirect if the button isn't present. */
    checkout: function () {
      var btn = $('form[action*="/cart"] button[name="checkout"]');
      if (btn) { btn.click(); return; }
      window.location.href = cfg.routes.checkout;
    },

    paint: function () {
      var c = Cart.state;
      if (!c) return;

      $$('[data-cart-count]').forEach(function (el) {
        el.textContent = c.item_count || '';
        el.dataset.count = c.item_count;
      });

      var body = $('[data-cart-body]');
      var foot = $('[data-cart-foot]');
      if (!body) return;

      if (!c.item_count) {
        body.innerHTML = $('#akq-cart-empty') ? $('#akq-cart-empty').innerHTML : '';
        if (foot) foot.hidden = true;
        document.dispatchEvent(new CustomEvent('akq:cart', { detail: c }));
        return;
      }
      if (foot) foot.hidden = false;

      body.innerHTML = c.items.map(lineHTML).join('');

      if (foot) {
        /* Update figures in place — never replace the foot's markup, or the
           checkout button loses the listeners Magic Checkout bound at load. */
        var toFree = Math.max(0, (cfg.freeShipAbove || 0) - c.total_price);
        var progress = $('[data-cart-progress]', foot);
        var fill     = $('[data-cart-progress-fill]', foot);
        var subtotal = $('[data-cart-subtotal]', foot);
        var label    = $('[data-cart-checkout-label]', foot);

        if (progress) {
          progress.innerHTML = toFree > 0
            ? 'Add <b>' + money(toFree) + '</b> for free delivery'
            : '<b>Free delivery</b> unlocked';
        }
        if (fill) {
          fill.style.width = Math.min(100, (c.total_price / (cfg.freeShipAbove || 1)) * 100) + '%';
        }
        if (subtotal) subtotal.textContent = money(c.total_price);
        if (label)    label.textContent = 'Checkout · ' + money(c.total_price);
      }

      document.dispatchEvent(new CustomEvent('akq:cart', { detail: c }));
    }
  };

  function lineHTML(item) {
    var img = item.image
      ? '<img src="' + item.image.replace(/(\.[a-z]+)(\?|$)/i, '_200x267$1$2') +
        '" alt="" loading="lazy" width="200" height="267">'
      : '';
    /* item.options_with_values gives Size / Colour without string-splitting
       the variant title, which breaks the moment an option contains a slash. */
    var opts = (item.options_with_values || [])
      .map(function (o) { return o.value; })
      .join(' · ');

    return '' +
      '<div class="line" data-key="' + item.key + '">' +
        '<div class="line__media">' + img + '</div>' +
        '<div class="line__body">' +
          '<p class="line__name"><a href="' + item.url + '">' + item.product_title + '</a></p>' +
          (opts ? '<p class="line__meta">' + opts + '</p>' : '') +
          '<div class="line__row">' +
            '<div class="qty">' +
              '<button data-qty="-1" aria-label="Decrease quantity">–</button>' +
              '<span>' + item.quantity + '</span>' +
              '<button data-qty="1" aria-label="Increase quantity">+</button>' +
            '</div>' +
            '<span class="price">' + money(item.final_line_price) + '</span>' +
          '</div>' +
          '<div class="line__row">' +
            '<button class="line__rm" data-rm>Remove</button>' +
          '</div>' +
        '</div>' +
      '</div>';
  }

  /* ----------------------------------------------------------------------
     Product form — variant selection.
     Options map to a real Shopify variant id; the hidden input is what gets
     POSTed, so an unavailable combination can never reach the cart.
     ---------------------------------------------------------------------- */
  function initProductForms() {
    $$('[data-product-form]').forEach(function (form) {
      var variants = JSON.parse(form.querySelector('[data-variants]').textContent);
      var idInput  = form.querySelector('[name="id"]');
      var addBtn   = form.querySelector('[data-add-to-cart]');
      var priceEl  = document.querySelector('[data-price-target]');
      var chosen   = {};

      function currentVariant() {
        var names = Object.keys(chosen);
        return variants.find(function (v) {
          return names.every(function (n) { return v.options[Number(n)] === chosen[n]; })
            && names.length === v.options.length;
        });
      }

      function sync() {
        var v = currentVariant();
        if (!v) { idInput.value = ''; return; }
        idInput.value = v.id;
        addBtn.disabled = !v.available;
        addBtn.querySelector('span').textContent = v.available
          ? addBtn.dataset.labelAdd
          : cfg.strings.sold_out;
        if (priceEl) priceEl.innerHTML =
          '<span class="price">' + money(v.price) + '</span>' +
          (v.compare_at_price > v.price
            ? ' <span class="price price--mrp">' + money(v.compare_at_price) + '</span>'
            : '');
        /* Keep the URL shareable and the back button honest. */
        var url = new URL(window.location);
        url.searchParams.set('variant', v.id);
        window.history.replaceState({}, '', url);
      }

      form.addEventListener('click', function (e) {
        var opt = e.target.closest('[data-option-index]');
        if (!opt || opt.disabled) return;
        e.preventDefault();
        var idx = opt.dataset.optionIndex;
        chosen[idx] = opt.dataset.value;
        $$('[data-option-index="' + idx + '"]', form).forEach(function (b) {
          b.classList.toggle('is-sel', b === opt);
        });
        var out = form.querySelector('[data-option-out="' + idx + '"]');
        if (out) out.textContent = opt.dataset.value;
        sync();
      });

      form.addEventListener('submit', function (e) {
        e.preventDefault();
        if (!idInput.value) { Toast.show(cfg.strings.pick_size); return; }
        var buyNow = e.submitter && e.submitter.hasAttribute('data-buy-now');
        Cart.add(idInput.value, 1, { silent: buyNow }).then(function (c) {
          if (c && buyNow) Cart.checkout();
        });
      });

      /* Preselect when there is only one value for an option, and honour
         ?variant= so a shared link lands on the right colour and size. */
      var pre = new URLSearchParams(location.search).get('variant');
      var preV = pre && variants.find(function (v) { return String(v.id) === pre; });
      $$('[data-option-index]', form).forEach(function (b) {
        var idx = b.dataset.optionIndex;
        var only = $$('[data-option-index="' + idx + '"]', form).length === 1;
        var match = preV && preV.options[Number(idx)] === b.dataset.value;
        if (only || match) b.click();
      });
    });
  }

  /* ----------------------------------------------------------------------
     Global delegated clicks
     ---------------------------------------------------------------------- */
  function initGlobal() {
    document.addEventListener('click', function (e) {
      var open = e.target.closest('[data-open]');
      if (open) { e.preventDefault(); return Drawer.open(open.dataset.open); }

      var close = e.target.closest('[data-close]');
      if (close) { e.preventDefault(); return Drawer.close(); }

      if (e.target.classList.contains('modal')) return Drawer.close();

      /* Quick add straight off a product card — the card carries a single
         variant id chosen in Liquid (first available). */
      var quick = e.target.closest('[data-quick-add]');
      if (quick && !quick.disabled) {
        e.preventDefault();
        return Cart.add(quick.dataset.quickAdd, 1);
      }

      var line = e.target.closest('.line');
      if (line) {
        var key = line.dataset.key;
        if (e.target.closest('[data-rm]')) return Cart.change(key, 0);
        var q = e.target.closest('[data-qty]');
        if (q) {
          var now = Number(line.querySelector('.qty span').textContent);
          return Cart.change(key, now + Number(q.dataset.qty));
        }
      }
    });
  }

  /* ----------------------------------------------------------------------
     Header — hide on scroll down, publish its height for sticky offsets
     ---------------------------------------------------------------------- */
  function initHeader() {
    var h = $('.header');
    if (!h) return;
    var last = 0;
    window.addEventListener('scroll', function () {
      var y = window.scrollY;
      h.classList.toggle('is-hidden', y > 200 && y > last);
      last = y;
    }, { passive: true });

    var setH = function () {
      document.documentElement.style.setProperty('--header-h', h.offsetHeight + 'px');
    };
    setH();
    if ('ResizeObserver' in window) new ResizeObserver(setH).observe(h);
    else window.addEventListener('resize', setH);
  }

  /* ----------------------------------------------------------------------
     Pincode check — prototype ETA, stored locally.
     [[LAUNCH]] swap the body of check() for the courier serviceability API.
     ---------------------------------------------------------------------- */
  function initPincode() {
    var form = $('[data-pin-form]');
    if (!form) return;
    var input = $('[data-pin-input]', form);
    var msg   = $('[data-pin-msg]', form);

    function paint() {
      var saved = null;
      try { saved = JSON.parse(localStorage.getItem('akq.pincode')); } catch (e) {}
      $$('[data-pin-line]').forEach(function (line) {
        line.innerHTML = saved
          ? '<b>Delivering to ' + saved.pin + '</b> <a href="#" data-open="pin">Change</a>'
          : '<b>Enter PIN code</b> <a href="#" data-open="pin">to check delivery &amp; COD</a>';
      });
      if (saved && input && !input.value) {
        input.value = saved.pin;
        msg.hidden = false; msg.dataset.state = 'ok';
        msg.textContent = 'Delivers in ' + saved.days + ' days to ' + saved.pin +
          ' · Cash on Delivery available.';
      }
    }
    paint();

    input.addEventListener('input', function () {
      input.value = input.value.replace(/\D/g, '').slice(0, 6);
    });

    form.addEventListener('submit', function (e) {
      e.preventDefault();
      var pin = input.value;
      if (!/^\d{6}$/.test(pin)) {
        msg.hidden = false; msg.dataset.state = 'err';
        msg.textContent = 'Enter a valid 6-digit PIN code.';
        input.focus();
        return;
      }
      var sum = pin.split('').reduce(function (a, d) { return a + Number(d); }, 0);
      var days = 3 + (sum % 4);
      try { localStorage.setItem('akq.pincode', JSON.stringify({ pin: pin, days: days })); } catch (e) {}
      msg.hidden = false; msg.dataset.state = 'ok';
      msg.textContent = 'Delivers in ' + days + ' days to ' + pin +
        ' · Cash on Delivery available.';
      paint();
    });
  }

  /* ----------------------------------------------------------------------
     Hero panel carousel — same filmstrip behaviour as the prototype
     ---------------------------------------------------------------------- */
  function initCarousel() {
    var root = $('[data-carousel]');
    if (!root) return;
    var track  = $('[data-carousel-track]', root);
    var slides = $$('.hero-c__slide', root);
    var dots   = $('[data-carousel-dots]', root);
    if (slides.length < 2) return;

    var i = 0, timer = null;
    var reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    var INTERVAL = Number(root.dataset.interval || 6000);
    root.style.setProperty('--hero-interval', INTERVAL + 'ms');

    dots.innerHTML = slides.map(function (_, n) {
      return '<button class="hero-c__dot' + (n === 0 ? ' is-on' : '') + '" role="tab" ' +
        'aria-label="Slide ' + (n + 1) + '" data-go="' + n + '"></button>';
    }).join('');

    function slide() {
      var s = slides[i], lastS = slides[slides.length - 1];
      var view = root.clientWidth;
      var span = lastS.offsetLeft + lastS.offsetWidth;
      var x = -(s.offsetLeft - (view - s.offsetWidth) / 2);
      x = Math.max(Math.min(0, view - span), Math.min(0, x));
      track.style.transform = 'translate3d(' + x + 'px,0,0)';
    }
    function go(n) {
      i = (n + slides.length) % slides.length;
      slides.forEach(function (s, k) { s.classList.toggle('is-active', k === i); });
      $$('.hero-c__dot', dots).forEach(function (d, k) { d.classList.toggle('is-on', k === i); });
      slide();
    }
    function play()  { if (!reduced && !timer) { root.classList.remove('is-paused'); timer = setInterval(function () { go(i + 1); }, INTERVAL); } }
    function pause() { root.classList.add('is-paused'); clearInterval(timer); timer = null; }

    dots.addEventListener('click', function (e) {
      var b = e.target.closest('[data-go]');
      if (b) { go(Number(b.dataset.go)); pause(); play(); }
    });
    var prev = $('[data-carousel-prev]', root), next = $('[data-carousel-next]', root);
    if (prev) prev.addEventListener('click', function () { go(i - 1); pause(); play(); });
    if (next) next.addEventListener('click', function () { go(i + 1); pause(); play(); });

    ['mouseenter', 'focusin'].forEach(function (ev) { root.addEventListener(ev, pause); });
    ['mouseleave', 'focusout'].forEach(function (ev) { root.addEventListener(ev, play); });

    var rt;
    window.addEventListener('resize', function () {
      clearTimeout(rt);
      track.style.transition = 'none';
      slide();
      rt = setTimeout(function () { track.style.transition = ''; }, 120);
    });

    go(0);
    play();
  }

  /* ----------------------------------------------------------------------
     Marquee, accordions, scroll reveal — presentation only
     ---------------------------------------------------------------------- */
  function initMisc() {
    $$('.marquee').forEach(function (m) {
      var t = $('.marquee__track', m);
      if (t && !t.dataset.done) { t.dataset.done = '1'; m.appendChild(t.cloneNode(true)); }
    });

    document.addEventListener('click', function (e) {
      var btn = e.target.closest('.acc__btn');
      if (!btn) return;
      var item = btn.closest('.acc__item');
      var open = item.classList.contains('is-open');
      if (btn.closest('[data-acc-single]')) {
        $$('.acc__item', btn.closest('[data-acc-single]')).forEach(function (x) {
          x.classList.remove('is-open');
          x.querySelector('.acc__btn').setAttribute('aria-expanded', 'false');
        });
      }
      item.classList.toggle('is-open', !open);
      btn.setAttribute('aria-expanded', String(!open));
    });

    var els = $$('.reveal');
    if (!('IntersectionObserver' in window)) {
      els.forEach(function (e) { e.classList.add('is-in'); });
    } else {
      var io = new IntersectionObserver(function (entries) {
        entries.forEach(function (en) {
          if (en.isIntersecting) { en.target.classList.add('is-in'); io.unobserve(en.target); }
        });
      }, { rootMargin: '0px 0px -8% 0px', threshold: 0.05 });
      els.forEach(function (e) { io.observe(e); });
    }

    /* Horizontal rails */
    $$('[data-rail]').forEach(function (sec) {
      var rail = $('.rail', sec);
      if (!rail) return;
      var step = function () { return rail.firstElementChild ? rail.firstElementChild.offsetWidth + 16 : 320; };
      var p = $('[data-rail-prev]', sec), n = $('[data-rail-next]', sec);
      if (p) p.addEventListener('click', function () { rail.scrollBy({ left: -step() * 2, behavior: 'smooth' }); });
      if (n) n.addEventListener('click', function () { rail.scrollBy({ left:  step() * 2, behavior: 'smooth' }); });
    });
  }

  /* Product recommendations — fetched client-side so the PDP's first paint
     never waits on Shopify's recommendation engine. */
  function initRecommendations() {
    $$('[data-recommendations-target]').forEach(function (el) {
      fetch(el.getAttribute('data-url'))
        .then(function (r) { return r.text(); })
        .then(function (html) {
          var doc = new DOMParser().parseFromString(html, 'text/html');
          var section = doc.querySelector('[data-rail]');
          if (section) el.replaceWith(section);
          else el.remove();
        })
        .catch(function () { el.remove(); });
    });
  }

  /* ---------------------------------------------------------------------- */
  function boot() {
    Drawer.init();
    initHeader();
    initGlobal();
    initProductForms();
    initPincode();
    initCarousel();
    initMisc();
    initRecommendations();
    Cart.fetch();
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();

  window.AKQTheme = { Cart: Cart, Drawer: Drawer, Toast: Toast, money: money };
})();
